import React, { useEffect, useRef } from 'react'
import MarketDepthChart from './MarketDepthChart'
import PriceHistoryChart from './PriceHistoryChart'
import { PlatformLogo } from './PlatformLogos'

interface MarketDrawerProps {
  isOpen: boolean
  onClose: () => void
  market: any
  enhancedData?: any
}

const MarketDrawer: React.FC<MarketDrawerProps> = ({ isOpen, onClose, market, enhancedData }) => {
  const drawerRef = useRef<HTMLDivElement>(null)
  const [childMarkets, setChildMarkets] = React.useState<any[]>([])
  const [loadingChildren, setLoadingChildren] = React.useState(false)
  const [priceHistory, setPriceHistory] = React.useState<any>(null)
  const [loadingHistory, setLoadingHistory] = React.useState(false)
  const [orderBook, setOrderBook] = React.useState<any>(null)
  
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    
    const handleClickOutside = (e: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.addEventListener('mousedown', handleClickOutside)
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])
  
  // Fetch child markets for group markets
  useEffect(() => {
    if (isOpen && market && market.market_type === 'group') {
      setLoadingChildren(true)
      fetch(`https://skybit-api.botnet-599.workers.dev/markets/parent/${market.id}`)
        .then(res => res.json())
        .then(data => {
          if (data.markets) {
            setChildMarkets(data.markets)
          }
        })
        .catch(err => {
          console.error('Failed to fetch child markets:', err)
        })
        .finally(() => {
          setLoadingChildren(false)
        })
    } else {
      setChildMarkets([])
    }
  }, [isOpen, market])
  
  // Process enhanced data for price history and order book
  useEffect(() => {
    if (enhancedData) {
      // Set price history if available
      if (enhancedData.priceHistory) {
        setPriceHistory({
          history: enhancedData.priceHistory
        })
      } else {
        setPriceHistory(null)
      }
      
      // Set order book if available
      if (enhancedData.orderBook) {
        setOrderBook(enhancedData.orderBook)
      } else {
        setOrderBook(null)
      }
      
      setLoadingHistory(false)
    } else {
      // Reset when no enhanced data
      setPriceHistory(null)
      setOrderBook(null)
      setLoadingHistory(false)
    }
  }, [enhancedData])
  
  if (!market) return null
  
  const formatPrice = (price?: number) => {
    if (price === undefined || price === null) return '-'
    // Show 2 decimal places for precision
    return `${(price * 100).toFixed(2)}%`
  }
  
  const formatNumber = (num: number) => {
    if (num === 0) return '$0'
    if (num >= 1000000000) return `$${(num / 1000000000).toFixed(1)}B`
    if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`
    if (num >= 10000) return `$${(num / 1000).toFixed(0)}K`
    if (num >= 1000) return `$${(num / 1000).toFixed(1)}K`
    return `$${num.toFixed(0)}`
  }
  
  const formatTimestamp = (timestamp?: string | number) => {
    if (!timestamp) return '-'
    
    let date: Date
    if (typeof timestamp === 'string') {
      date = new Date(timestamp)
    } else {
      date = new Date(timestamp * 1000)
    }
    
    const now = new Date()
    const diff = date.getTime() - now.getTime()
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    
    if (days > 0) return `${days}d ${hours}h`
    if (hours > 0) return `${hours}h`
    return 'Soon'
  }
  
  const getMarketUrl = () => {
    // Use the URL field if available
    if (market.url) {
      return market.url
    }
    
    // Fallback to manual generation
    switch (market.platform) {
      case 'polymarket':
        return `https://polymarket.com/event/${market.slug || market.condition_id}`
      case 'kalshi':
        // Construct Kalshi URL directly like we do for Polymarket
        let kalshiTicker = market.ticker || market.original_id
        
        // For group markets, extract ticker from ID if ticker is not available
        if (!kalshiTicker && market.id) {
          if (market.id.startsWith('kalshi_event_')) {
            kalshiTicker = market.id.replace('kalshi_event_', '')
          } else if (market.id.startsWith('kalshi_group_')) {
            kalshiTicker = market.id.replace('kalshi_group_', '')
          } else {
            kalshiTicker = market.id
          }
        }
        
        if (!kalshiTicker) {
          return null
        }
        
        // Simple direct URL construction
        return `https://kalshi.com/markets/${kalshiTicker.toLowerCase()}`
      case 'predictit':
        // For PredictIt, extract the market ID from composite IDs
        // Format: predictit_{marketId}_{contractId} or predictit_group_{marketId}
        let marketId = market.id
        
        // Use original_id first if available as it's cleaner
        if (market.original_id) {
          // If original_id contains underscore, it's {marketId}_{contractId}
          if (market.original_id.includes('_')) {
            marketId = market.original_id.split('_')[0]
          } else {
            marketId = market.original_id
          }
        } else if (market.id.startsWith('predictit_group_')) {
          marketId = market.id.replace('predictit_group_', '')
        } else if (market.id.startsWith('predictit_')) {
          // Extract market ID from predictit_{marketId}_{contractId}
          const parts = market.id.replace('predictit_', '').split('_')
          marketId = parts[0]
        }
        
        return `https://www.predictit.org/markets/detail/${marketId}`
      case 'limitless':
        return `https://limitless.exchange/markets/${market.slug || market.id}`
      default:
        return null
    }
  }
  
  const marketUrl = getMarketUrl()
  const isGroup = market.market_type === 'group'
  const spread = market.yes_price !== undefined && market.no_price !== undefined 
    ? Math.abs((market.yes_price + market.no_price) - 1)
    : null
  
  return (
    <>
      {/* Invisible backdrop for click detection */}
      <div 
        className={`drawer-backdrop ${isOpen ? 'open' : ''}`}
        aria-hidden="true"
      />
      
      {/* Drawer */}
      <div 
        ref={drawerRef}
        className={`market-drawer ${isOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
      >
        {/* Header */}
        <div className="drawer-header">
          <div className="drawer-header-left">
            <PlatformLogo platform={market.platform} size={24} showText={false} />
            <div className="drawer-header-text">
              <span className="drawer-platform-name">{market.platform}</span>
              <span className="drawer-market-id">{market.ticker || market.id}</span>
            </div>
          </div>
          <button 
            className="drawer-close"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="drawer-content">
          {/* Question Section */}
          <div className="drawer-question-section">
            <h2 className="drawer-question">
              {market.is_series && (
                <span 
                  title="This is a recurring periodic market with similar structure"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '20px',
                    height: '20px',
                    borderRadius: '3px',
                    backgroundColor: 'rgba(var(--sacred-accent-rgb), 0.1)',
                    color: 'var(--sacred-accent)',
                    fontSize: '14px',
                    marginRight: '8px',
                    cursor: 'help'
                  }}
                >
                  ↻
                </span>
              )}
              {market.question}
            </h2>
            {market.category && (
              <div className="drawer-category">{market.category}</div>
            )}
          </div>
          
          {/* Price Section */}
          {!isGroup && (
            <div className="drawer-price-section">
              {/* Show outcomes for group markets */}
              {market.outcomes && market.outcomes.length > 0 ? (
                <div className="drawer-outcomes">
                  <h3 className="outcomes-title">Market Outcomes</h3>
                  <div className="group-markets-table">
                    <table className="child-markets-table">
                      <thead>
                        <tr>
                          <th>Outcome</th>
                          <th>Price</th>
                          <th>Probability</th>
                          <th>Volume</th>
                        </tr>
                      </thead>
                      <tbody>
                        {market.outcomes.map((outcome: any, idx: number) => (
                          <tr key={idx} className="child-market-row">
                            <td className="outcome-name-cell">
                              <div className="outcome-info">
                                <span className="outcome-name">{outcome.name}</span>
                                {outcome.token_id && (
                                  <span className="outcome-ticker" style={{ fontSize: '0.65rem', color: 'var(--sacred-text-dim)', marginLeft: '0.5rem' }}>
                                    {outcome.token_id}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="price-cell">
                              <div className="price-box">
                                <span className="price-value">{formatPrice(outcome.price)}</span>
                              </div>
                            </td>
                            <td className="price-cell">
                              <span style={{ color: 'var(--sacred-text-dim)', fontSize: '0.75rem' }}>
                                {((outcome.price || 0) * 100).toFixed(2)}%
                              </span>
                            </td>
                            <td className="volume-cell">
                              {formatNumber(outcome.volume || 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : market.yes_price !== undefined && market.no_price !== undefined ? (
                <>
                  <div className="drawer-binary-prices">
                    <div className="binary-price-card yes">
                      <div className="price-header">
                        <span className="price-label">YES</span>
                        <span className="price-prob">{(market.yes_price * 100).toFixed(2)}%</span>
                      </div>
                      <div className="price-amount">{formatPrice(market.yes_price)}</div>
                    </div>
                    <div className="binary-price-card no">
                      <div className="price-header">
                        <span className="price-label">NO</span>
                        <span className="price-prob">{(market.no_price * 100).toFixed(2)}%</span>
                      </div>
                      <div className="price-amount">{formatPrice(market.no_price)}</div>
                    </div>
                  </div>
                  
                  {/* Price Bar Visualization */}
                  <div className="price-bar-container">
                    <div className="price-bar">
                      <div 
                        className="price-bar-yes" 
                        style={{ width: `${market.yes_price * 100}%` }}
                      >
                        <span className="price-bar-label">{(market.yes_price * 100).toFixed(0)}%</span>
                      </div>
                      <div 
                        className="price-bar-no" 
                        style={{ width: `${market.no_price * 100}%` }}
                      >
                        <span className="price-bar-label">{(market.no_price * 100).toFixed(0)}%</span>
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          )}
          
          {/* Stats Grid */}
          <div className="drawer-stats-section">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-content">
                  <div className="stat-label">Volume</div>
                  <div className="stat-value">{formatNumber(market.volume || 0)}</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-content">
                  <div className="stat-label">Liquidity</div>
                  <div className="stat-value">{formatNumber(market.liquidity || 0)}</div>
                </div>
              </div>
              {spread !== null && (
                <div className="stat-card">
                  <div className="stat-content">
                    <div className="stat-label">Spread</div>
                    <div className="stat-value">{(spread * 100).toFixed(2)}%</div>
                  </div>
                </div>
              )}
              <div className="stat-card">
                <div className="stat-content">
                  <div className="stat-label">Closes</div>
                  <div className="stat-value">{formatTimestamp(market.end_date)}</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Charts Section */}
          {!isGroup && (market.platform === 'polymarket' || market.platform === 'kalshi') && (
            <>
              {/* Price History Chart */}
              {(priceHistory || loadingHistory) && (
                <div className="drawer-chart-section">
                  <h3 className="chart-title">Price History</h3>
                  <div className="drawer-chart">
                    {loadingHistory ? (
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        height: '200px',
                        color: 'var(--sacred-text-dim)',
                        fontSize: '0.65rem'
                      }}>
                        Loading...
                      </div>
                    ) : priceHistory ? (
                      <PriceHistoryChart 
                        data={priceHistory} 
                        platform={market.platform}
                      />
                    ) : null}
                  </div>
                </div>
              )}
              
              {/* Order Book Chart */}
              {enhancedData && enhancedData.orderBook && (
                <div className="drawer-chart-section">
                  <h3 className="chart-title">Order Book</h3>
                  <div className="drawer-chart">
                    <MarketDepthChart 
                      marketData={enhancedData} 
                      platform={market.platform}
                      mode="all"
                    />
                  </div>
                </div>
              )}
            </>
          )}
          
          {/* Group Markets */}
          {isGroup && (
            <div className="drawer-group-section">
              <h3 className="group-title">
                Market Outcomes
                {loadingChildren && <span style={{ fontSize: '0.7rem', marginLeft: '0.5rem', color: 'var(--sacred-text-dim)' }}>(loading...)</span>}
              </h3>
              {!loadingChildren && childMarkets.length === 0 && market.outcomes && market.outcomes.length > 0 && (
                // For Polymarket markets with outcomes array
                <div className="group-markets-table">
                  <table className="child-markets-table">
                    <thead>
                      <tr>
                        <th>Outcome</th>
                        <th>Price</th>
                        <th>Volume</th>
                      </tr>
                    </thead>
                    <tbody>
                      {market.outcomes.map((outcome: any, idx: number) => (
                        <tr key={idx} className="child-market-row">
                          <td className="outcome-name-cell">
                            <div className="outcome-info">
                              <span className="outcome-name">{outcome.name}</span>
                            </div>
                          </td>
                          <td className="price-cell">
                            <div className="price-box">
                              <span className="price-value">{formatPrice(outcome.price)}</span>
                            </div>
                          </td>
                          <td className="volume-cell">
                            {formatNumber(outcome.volume || 0)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {!loadingChildren && childMarkets.length === 0 && (!market.outcomes || market.outcomes.length === 0) && (
                <div style={{ color: 'var(--sacred-text-dim)', fontSize: '0.8rem' }}>
                  No related markets found
                </div>
              )}
              {childMarkets.length > 0 && (
                <div className="group-markets-table">
                  <table className="child-markets-table">
                    <thead>
                      <tr>
                        <th>Outcome</th>
                        <th>Yes</th>
                        <th>No</th>
                        <th>Volume</th>
                      </tr>
                    </thead>
                    <tbody>
                      {childMarkets.map((subMarket: any) => {
                        // Use backend-provided title fields in priority order
                        let outcomeName = subMarket.question
                        
                        // For Kalshi markets, use the yes_sub_title field which contains the actual outcome
                        if (market.platform === 'kalshi' && subMarket.yes_sub_title) {
                          outcomeName = subMarket.yes_sub_title
                        } 
                        // For Polymarket, use group_item_title if available
                        else if (market.platform === 'polymarket' && subMarket.group_item_title) {
                          outcomeName = subMarket.group_item_title
                        }
                        // For all platforms, check event_title
                        else if (subMarket.event_title && subMarket.event_title !== market.question) {
                          outcomeName = subMarket.event_title
                        }
                        // Otherwise keep the question as-is
                        
                        return (
                          <tr key={subMarket.id} className="child-market-row">
                            <td className="outcome-name-cell">
                              <div className="outcome-info">
                                <span className="outcome-name">{outcomeName}</span>
                                {subMarket.ticker && (
                                  <span className="outcome-ticker">{subMarket.ticker}</span>
                                )}
                              </div>
                            </td>
                            <td className="price-cell yes">
                              <div className="price-box yes-box">
                                <span className="price-label">Yes</span>
                                <span className="price-value">{formatPrice(subMarket.yes_price)}</span>
                              </div>
                            </td>
                            <td className="price-cell no">
                              <div className="price-box no-box">
                                <span className="price-label">No</span>
                                <span className="price-value">{formatPrice(subMarket.no_price)}</span>
                              </div>
                            </td>
                            <td className="volume-cell">
                              {formatNumber(subMarket.volume || 0)}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          
          {/* Action Button */}
          <div className="drawer-action-section">
            <a 
              href={marketUrl || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="drawer-trade-btn"
            >
              <span>Trade on {market.platform}</span>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M1 13L13 1M13 1H3M13 1V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </>
  )
}

export default MarketDrawer